import React from 'react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  ChoiceField,
  missingOption,
  useDependsOn,
  useDroppedRowsWarning,
  wrongShape,
} from '@ns/plugin-picker-shared';

import { queryChoiceState } from './query-state';
import { resourceGroupNameFrom, subscriptionIdFrom } from './refs';
import {
  buildResourceQuery,
  describeMalformedResourceType,
  isResourceTypeShaped,
  type ResourcePickerUiOptions,
  resourceQueryEnabled,
  selectResources,
} from './resource-query';
import { AZURE_PICKER_API_VERSION } from './subscription-query';
import type { AzureResource } from './types';
import { useQueryResources } from './use-query-resources';

/**
 * Picks a resource of one kind, within a resource group chosen elsewhere.
 *
 * @remarks
 * All three options are required and none has a default. A default would save
 * lines and cost an author who named his field something else an error about a
 * field name he never typed.
 */
export function AzureResourcePickerComponent(
  props: FieldExtensionComponentProps<AzureResource, ResourcePickerUiOptions>,
): React.ReactElement {
  const uiOptions = props.uiSchema['ui:options'];
  const { resourceGroupRef, resourceType, subscriptionRef } = uiOptions ?? {};

  const refs = [subscriptionRef, resourceGroupRef].filter(
    (ref): ref is string => ref !== undefined,
  );
  const state = useDependsOn(props, refs);

  const subscriptionId = subscriptionIdFrom(state, subscriptionRef);
  const resourceGroup = resourceGroupNameFrom(state, resourceGroupRef);

  // All three keys are required, and each mistake is knowable without fetching
  // anything, so it is reported rather than silently leaving the field off
  //.
  const authorError = React.useMemo(() => {
    const required: readonly [string, string | undefined, string][] = [
      [
        'subscriptionRef',
        subscriptionRef,
        'it names the field holding the subscription',
      ],
      [
        'resourceGroupRef',
        resourceGroupRef,
        'it names the field holding the resource group',
      ],
      [
        'resourceType',
        resourceType,
        'without a kind there is no sensible list, only everything at once',
      ],
    ];

    const absent = required.find(([, written]) => written === undefined);
    if (absent !== undefined) {
      return missingOption(absent[0], absent[2]);
    }

    // All three take one piece of text. A list or an object there is a mistake
    // the compiler cannot catch, because a template is YAML.
    const misshapen = required.find(
      ([, written]) => typeof written !== 'string',
    );
    if (misshapen !== undefined) {
      return wrongShape(
        misshapen[0],
        misshapen[0] === 'resourceType'
          ? 'one piece of text, such as Microsoft.Network/routeTables'
          : 'one field name, written as a plain word',
      );
    }

    return resourceType !== undefined && !isResourceTypeShaped(resourceType)
      ? describeMalformedResourceType(resourceType)
      : undefined;
  }, [resourceGroupRef, resourceType, subscriptionRef]);

  const enabled =
    authorError === undefined &&
    resourceQueryEnabled({ resourceGroup, subscriptionId });

  // Building happens on every render, so it must not run on options already
  // known to be wrong: a `resourceType` that is an object rather than text
  // makes the literal builder throw, and a throw here tears down the form.
  const query =
    authorError === undefined
      ? buildResourceQuery({
          resourceGroup: resourceGroup ?? '',
          resourceType: resourceType ?? '',
        })
      : '';

  const resources = useQueryResources(
    {
      apiVersion: AZURE_PICKER_API_VERSION,
      query,
      subscriptions: subscriptionId === undefined ? [] : [subscriptionId],
    },
    { enabled, select: selectResources },
  );
  const answer = resources.data;
  useDroppedRowsWarning(
    'Azure resources',
    answer?.droppedRowCount,
    resources.dataUpdatedAt,
  );

  const selected = props.formData;
  const selectedKey =
    typeof selected?.id === 'string' ? selected.id.toLowerCase() : null;

  return (
    <ChoiceField<AzureResource>
      description={props.schema.description}
      // Naming the type turns a typo from an afternoon into ten seconds.
      emptyText={
        typeof resourceType === 'string'
          ? `No resources of type "${resourceType}"`
          : 'No resources'
      }
      getKey={item => item.id.toLowerCase()}
      getLabel={item => item.name}
      items={answer?.items}
      label={props.schema.title ?? props.name}
      onSelect={item => props.onChange(item)}
      required={props.required}
      selectedKey={selectedKey}
      selectedLabel={typeof selected?.name === 'string' ? selected.name : null}
      sourceKey={query}
      state={queryChoiceState({
        authorError,
        dependsOn: state,
        droppedRowCount: answer?.droppedRowCount,
        error: resources.error,
        hasItems: (answer?.items.length ?? 0) > 0,
        isError: resources.isError,
        isPending: enabled && resources.isPending,
        page: answer?.page,
        required: props.required,
        retry: () => void resources.refetch(),
      })}
    />
  );
}
